"use client"

import * as React from "react"

import { cn } from "@/lib/utils"
import { useMediaQuery } from "@/hooks/useMediaQuery"
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerFooter,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from "@/components/ui/drawer"

interface ResponsiveModalProps {
    children: React.ReactNode
    open?: boolean
    onOpenChange?: (open: boolean) => void
}

interface ResponsiveModalContentProps {
    children: React.ReactNode
    className?: string
    /** Size variant: 'sm' for settings, 'lg' for lists, 'xl' for forms */
    size?: 'sm' | 'md' | 'lg' | 'xl'
}

interface ResponsiveModalHeaderProps {
    children: React.ReactNode
    className?: string
}

interface ResponsiveModalTitleProps {
    children: React.ReactNode
    className?: string
}

interface ResponsiveModalDescriptionProps {
    children: React.ReactNode
    className?: string
}

interface ResponsiveModalFooterProps {
    children: React.ReactNode
    className?: string
}

const ResponsiveModalContext = React.createContext<{
    isDesktop: boolean
}>({ isDesktop: false })

function ResponsiveModal({ children, open, onOpenChange }: ResponsiveModalProps) {
    const isDesktop = useMediaQuery("(min-width: 768px)")

    if (isDesktop) {
        return (
            <ResponsiveModalContext.Provider value={{ isDesktop: true }}>
                <Dialog open={open} onOpenChange={onOpenChange}>
                    {children}
                </Dialog>
            </ResponsiveModalContext.Provider>
        )
    }

    return (
        <ResponsiveModalContext.Provider value={{ isDesktop: false }}>
            <Drawer open={open} onOpenChange={onOpenChange}>
                {children}
            </Drawer>
        </ResponsiveModalContext.Provider>
    )
}

function ResponsiveModalTrigger({ children, className }: { children: React.ReactNode; className?: string }) {
    const { isDesktop } = React.useContext(ResponsiveModalContext)

    if (isDesktop) {
        return <DialogTrigger className={className} asChild>{children}</DialogTrigger>
    }

    return <DrawerTrigger className={className} asChild>{children}</DrawerTrigger>
}

function ResponsiveModalContent({ children, className, size = 'lg' }: ResponsiveModalContentProps) {
    const { isDesktop } = React.useContext(ResponsiveModalContext)

    // Size mapping for desktop dialogs
    const sizeClasses = {
        sm: 'sm:max-w-[400px]',
        md: 'sm:max-w-[500px]',
        lg: 'sm:max-w-[672px]', // max-w-2xl
        xl: 'sm:max-w-[800px]',
    }

    if (isDesktop) {
        return (
            <DialogContent className={cn(sizeClasses[size], className)}>
                {children}
            </DialogContent>
        )
    }

    return (
        <DrawerContent className={cn("max-h-[85vh]", className)}>
            <div className="w-full overflow-y-auto">{children}</div>
        </DrawerContent>
    )
}

function ResponsiveModalHeader({ children, className }: ResponsiveModalHeaderProps) {
    const { isDesktop } = React.useContext(ResponsiveModalContext)

    if (isDesktop) {
        return <DialogHeader className={className}>{children}</DialogHeader>
    }

    return <DrawerHeader className={cn("text-left", className)}>{children}</DrawerHeader>
}

function ResponsiveModalTitle({ children, className }: ResponsiveModalTitleProps) {
    const { isDesktop } = React.useContext(ResponsiveModalContext)

    if (isDesktop) {
        return <DialogTitle className={className}>{children}</DialogTitle>
    }

    return <DrawerTitle className={className}>{children}</DrawerTitle>
}

function ResponsiveModalDescription({ children, className }: ResponsiveModalDescriptionProps) {
    const { isDesktop } = React.useContext(ResponsiveModalContext)

    if (isDesktop) {
        return <DialogDescription className={className}>{children}</DialogDescription>
    }

    return <DrawerDescription className={className}>{children}</DrawerDescription>
}

function ResponsiveModalFooter({ children, className }: ResponsiveModalFooterProps) {
    const { isDesktop } = React.useContext(ResponsiveModalContext)

    if (isDesktop) {
        return <DialogFooter className={className}>{children}</DialogFooter>
    }

    return <DrawerFooter className={className}>{children}</DrawerFooter>
}

function ResponsiveModalClose({ children, className }: { children?: React.ReactNode; className?: string }) {
    const { isDesktop } = React.useContext(ResponsiveModalContext)

    if (isDesktop) {
        return <DialogClose className={className}>{children}</DialogClose>
    }

    return <DrawerClose className={className}>{children}</DrawerClose>
}

export {
    ResponsiveModal,
    ResponsiveModalTrigger,
    ResponsiveModalContent,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalClose,
}
